import React, { createContext, useState, useEffect } from 'react';
import { User } from '@havenly/shared';

export interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      fetchUserProfile(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchUserProfile = async (accessToken: string) => {
    try {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:8787'}/api/auth/me`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setUser(data.data);
          setIsLoading(false);
          return;
        }
      } catch {
        // If API fails, continue with localStorage fallback
      }

      // Fallback: Use localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:8787'}/api/auth/login`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          const { accessToken, user: userData } = data.data;

          setToken(accessToken);
          setUser(userData);
          localStorage.setItem('token', accessToken);
          localStorage.setItem('user', JSON.stringify(userData));
          setIsLoading(false);
          return;
        }
      } catch {
        // If API fails, continue with demo login
      }

      // Demo mode: Check demo accounts
      const demoAccountsStr = localStorage.getItem('demo-accounts');
      if (demoAccountsStr) {
        const demoAccounts = JSON.parse(demoAccountsStr);
        const account = demoAccounts.find((acc: any) => acc.email === email && acc.password === password);
        if (account) {
          const token = 'demo-token-' + Date.now();
          const userData: User = {
            id: Math.floor(Math.random() * 10000),
            email: account.email,
            fullName: account.name,
            role: 'Owner',
            status: 'active',
            createdAt: new Date().toISOString(),
          };
          setToken(token);
          setUser(userData);
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(userData));
          setIsLoading(false);
          return;
        }
      }

      throw new Error('Invalid email or password');
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
